import type { File_, ResourceCredential, ResourceDraft } from '../api';
import { TruvityClient } from '../Client';
import { UnknownDraftCredential } from './credential/draft/unknown';
import { DraftCredentialWithClaims } from './credential/draft/with-claims';
import { Claims, DraftMetadata } from './credential/types';
import { UnknownVerifiableCredential } from './credential/verifiable/unknown';
import { VerifiableCredentialWithClaims } from './credential/verifiable/with-claims';
import { LinkedCredential, LinkedFile, Rfc3339Date, Rfc3339DateTime } from './models';

export { Claims };

export type Ctor<T> = new (...args: any[]) => T;

export type BlobInput = ArrayBufferView | ArrayBuffer | ReadableStream | string;

export type FileBlob = Blob;

export type ValueWithId = {
    id: string;
    digestMultibase?: string;
};

export type ValueWithDataId = {
    data: ValueWithId;
};

export const isValueWithDataId = (value: any): value is ValueWithDataId => {
    if (isValueWithId(value?.data)) {
        return true;
    }
    return false;
};

export const isValueWithId = (value: any): value is ValueWithId => {
    if (value === null) return false;
    if (typeof value !== 'object') return false;
    if (!Reflect.has(value, 'id')) return false;
    if (typeof Reflect.get(value, 'id') !== 'string') return false;

    if (Reflect.has(value, 'digestMultibase')) {
        if (typeof Reflect.get(value, 'digestMultibase') !== 'string') return false;
    }

    return true;
};

export type LinkedCredentialDescriptor = ValueWithId & {
    type: 'LinkedCredential';
};

export type LinkedFileDescriptor = ValueWithId & {
    type: 'LinkedFile';
};

export type LinkedEntityDescriptor = LinkedCredentialDescriptor | LinkedFileDescriptor;

export const isLinkedEntityDescriptor = (value: any): value is LinkedEntityDescriptor => {
    if (!isValueWithId(value)) return false;
    if (Reflect.get(value, 'type') !== 'LinkedCredential' && Reflect.get(value, 'type') !== 'LinkedFile') return false;
    return true;
};

type HandleClaimValue<T extends Claims, NestedT> = Exclude<T, undefined> extends LinkedCredential<infer V>
    ?
          | ValueWithId
          | LinkedCredential<V>
          | (V extends Claims ? VerifiableCredentialWithClaims<V> : UnknownVerifiableCredential<V>)
          | (V extends Claims ? DraftCredentialWithClaims<V> : UnknownDraftCredential<V>)
    : Exclude<T, undefined> extends LinkedFile
      ? ValueWithId | LinkedFile
      : T extends Rfc3339Date
        ? Rfc3339Date
        : T extends Rfc3339DateTime
          ? Rfc3339DateTime
          : T extends Claims
            ? NestedT
            : T;

export type UserUpdateClaims<T extends Claims> = {
    [P in keyof T]?: HandleClaimValue<T[P], UserUpdateClaims<T[P]>>;
};

export type UserIssueClaims<T extends Claims> = {
    [P in keyof T]: HandleClaimValue<T[P], UserIssueClaims<T[P]>>;
};

// In `ReadDraftClaims` all properties are optional
export type ReadDraftClaims<T extends Claims> = {
    [P in keyof T]?: Exclude<T[P], undefined> extends LinkedCredential<any> | LinkedFile
        ? T[P]
        : T[P] extends Claims
          ? ReadDraftClaims<T[P]>
          : T[P];
};

// In `ReadClaims` all properties have the same optionality as `Claims`
export type ReadClaims<T extends Claims> = {
    [P in keyof T]: Exclude<T[P], undefined> extends LinkedCredential<any> | LinkedFile
        ? T[P]
        : T[P] extends Claims
          ? ReadClaims<T[P]>
          : T[P];
};

type BaseDescriptor = {
    id: string;
    etag: string;
    revision: number;
};

export type DraftDescriptor = BaseDescriptor;

export type CredentialDescriptor = BaseDescriptor & {
    data: {
        id: string;
        digestMultibase?: string;
    };
};

export type PresentationDescriptor = BaseDescriptor & {
    data: {
        linkedCredentials: string[];
    };
};

export type FileDescriptor = BaseDescriptor & {
    data: File_;
};

export type UpdatePayload = {
    annotations?: Record<string, string>;
    labels?: Record<string, string>;
};

export type UpdatePayloadWithIssueClaims<T extends Claims> = UpdatePayload & {
    claims: UserIssueClaims<T>;
    metadata?: DraftMetadata;
};

export type VerificationResult = {
    verified: boolean;
};

export type ApiClient = Pick<
    TruvityClient,
    'desk' | 'dids' | 'didcommMessages' | 'drafts' | 'credentials' | 'presentations' | 'files' | 'schemas'
> & {
    tenantId: () => Promise<string>;
};

export const isResourceCredential = (input: ResourceDraft | ResourceCredential): input is ResourceCredential =>
    'id' in input.data;
